<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('poster.title') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-button 
                type="primary" 
                @click="openUploadDialog"
                :icon="Upload"
              >
                {{ $t('poster.uploadPoster') }}
              </el-button>
              <el-button 
                @click="loadPosters(true)"
                :icon="Refresh"
              >
                {{ $t('poster.refresh') }}
              </el-button>
            </div>
            <el-button-group class="view-toggle">
              <el-button 
                :type="viewMode === 'list' ? 'primary' : ''"
                @click="viewMode = 'list'"
              >
                <el-icon><List /></el-icon>
              </el-button>
              <el-button 
                :type="viewMode === 'card' ? 'primary' : ''"
                @click="viewMode = 'card'"
              >
                <el-icon><Grid /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </template>

      <!-- Swiper Demo - Mobile Preview -->
      <div v-if="posters.length > 0" class="swiper-demo-section">
        <div class="demo-header">
          <h3 class="demo-title">{{ $t('poster.swiperPreview') }}</h3>
          <span class="demo-subtitle">{{ $t('poster.mobilePreview') }}</span>
        </div>
        <div class="swiper-container">
          <el-carousel 
            :interval="4000" 
            height="200px"
            indicator-position="outside"
            arrow="always"
          >
            <el-carousel-item v-for="poster in posters" :key="poster.id">
              <div class="swiper-slide">
                <ImagePreview 
                  v-if="poster.url || poster.avatar" 
                  :src="poster.url || poster.avatar" 
                  :file-name="poster.fileName"
                  width="100%" 
                  height="100%"
                  :enable-preview="true"
                  fit="contain"
                />
                <div v-else class="slide-placeholder">
                  <el-icon class="placeholder-icon"><Picture /></el-icon>
                  <div class="placeholder-text">{{ $t('poster.noImage') }}</div>
                </div>
              </div>
            </el-carousel-item>
          </el-carousel>
        </div>
      </div>

      <!-- List View -->
      <el-table 
        v-if="viewMode === 'list'"
        :data="posters" 
        v-loading="loading" 
        stripe
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="avatar" :label="$t('poster.image')" width="100">
          <template #default="{ row }">
            <ImagePreview 
              v-if="row.url || row.avatar" 
              :src="row.url || row.avatar" 
              :file-name="row.fileName"
              width="60px" 
              height="60px"
              :enable-preview="true"
              fit="cover"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="displayName" :label="$t('poster.displayName')" min-width="150">
          <template #default="{ row }">
            {{ row.displayName || $t('poster.unnamed') }}
          </template>
        </el-table-column>
        <el-table-column prop="displayOrder" :label="$t('poster.order')" width="100" />
        <el-table-column prop="createdAt" :label="$t('common.createdAt')" min-width="150">
          <template #default="{ row }">
            <div class="date-cell">
              <div v-if="row.createdAt">{{ formatDate(row.createdAt) }}</div>
              <span v-else>-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="editPoster(row)">
              {{ $t('poster.actions.edit') }}
            </el-button>
            <el-button 
              type="danger" 
              link 
              @click="deletePoster(row)"
              :loading="deleting[row.id]"
            >
              {{ deleting[row.id] ? $t('poster.actions.deleting') : $t('poster.actions.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Card View -->
      <div v-else class="posters-grid" v-loading="loading" :element-loading-text="$t('poster.loadingPosters')">
        <div v-if="posters.length === 0" class="empty-state">
          <div class="empty-content">
            <el-icon class="empty-icon"><Picture /></el-icon>
            <h3 class="empty-title">{{ $t('poster.noPosters') }}</h3>
            <p class="empty-description">{{ $t('poster.uploadFirst') }}</p>
            <el-button 
              type="primary" 
              @click="openUploadDialog"
              style="margin-top: 16px"
            >
              {{ $t('poster.uploadFirstBtn') }}
            </el-button>
          </div>
        </div>

        <div v-else class="poster-cards">
          <div v-for="poster in posters" :key="poster.id" class="poster-card">
            <div class="poster-image-wrapper">
              <div v-if="poster.url || poster.avatar" class="image-container">
                <ImagePreview 
                  :src="poster.url || poster.avatar" 
                  :file-name="poster.fileName"
                  width="100%" 
                  height="240px"
                  :enable-preview="true"
                  fit="cover"
                />
              </div>
              <div v-else class="image-placeholder">
                <el-icon class="placeholder-icon"><Picture /></el-icon>
                <div class="placeholder-text">{{ $t('poster.noImage') }}</div>
              </div>
            </div>
            
            <div class="poster-info">
              <div class="poster-name">{{ poster.displayName || $t('poster.unnamed') }}</div>
              <div class="poster-meta">

                <span class="meta-item" v-if="poster.displayOrder !== undefined">
                  <el-icon><List /></el-icon>
                  {{ $t('poster.order') }}: {{ poster.displayOrder }}
                </span>
                <span class="meta-item" v-if="poster.createdAt">
                  <el-icon><Clock /></el-icon>
                  {{ formatDate(poster.createdAt) }}
                </span>
              </div>
              <div class="poster-actions">
                <div class="action-buttons">
                  <el-button 
                    type="primary" 
                    size="small" 
                    @click="editPoster(poster)"
                    :disabled="deleting[poster.id]"
                  >
                    <el-icon><Edit /></el-icon>
                    {{ $t('poster.actions.edit') }}
                  </el-button>
                  <el-button 
                    type="danger" 
                    size="small" 
                    @click="deletePoster(poster)"
                    :loading="deleting[poster.id]"
                    :disabled="deleting[poster.id]"
                  >
                    <el-icon v-if="!deleting[poster.id]"><Delete /></el-icon>
                    {{ deleting[poster.id] ? $t('poster.actions.deleting') : $t('poster.actions.delete') }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- Upload Dialog -->
    <el-dialog 
      v-model="showDialog" 
      :title="$t('poster.uploadDialog.title')"
      width="500px"
      :close-on-click-modal="false"
      class="upload-dialog"
    >
      <div class="upload-content">
        <!-- Upload Area or Image Preview -->
        <div v-if="!previewImage" class="upload-area">
          <el-upload
            class="upload-trigger"
            :show-file-list="false"
            :auto-upload="false"
            :on-change="handleFileSelect"
            accept="image/*"
            :drag="true"
            :disabled="uploading"
          >
            <div class="upload-placeholder">
              <el-icon><Upload /></el-icon>
              <div class="upload-text">{{ $t('poster.uploadDialog.clickOrDrag') }}</div>
              <div class="upload-hint">{{ $t('poster.uploadDialog.fileHint') }}</div>
            </div>
          </el-upload>
        </div>

        <!-- Image Preview -->
        <div v-else class="preview-wrapper">
          <ImagePreview 
            :src="previewImage" 
            width="100%" 
            height="300px"
            fit="contain"
            :enable-preview="true"
          />
          <el-button 
            class="remove-image-btn" 
            type="danger" 
            size="small" 
            @click="removeImage"
            :disabled="uploading"
          >
            <el-icon><Delete /></el-icon>
            {{ $t('poster.uploadDialog.removeImage') }}
          </el-button>
        </div>

        <!-- File Name Input -->
        <el-form :model="uploadForm" label-width="100px" class="upload-form">
          <el-form-item :label="$t('poster.uploadDialog.displayName')" prop="displayName">
            <el-input 
              v-model="uploadForm.displayName" 
              :placeholder="$t('poster.uploadDialog.displayNamePlaceholder')"
              clearable
              :disabled="!selectedFile || uploading"
            />
          </el-form-item>
          <el-form-item :label="$t('poster.uploadDialog.displayOrder')" prop="displayOrder">
            <el-input-number 
              v-model="uploadForm.displayOrder" 
              :min="0" 
              :max="9999"
              style="width: 100%"
              :placeholder="$t('poster.uploadDialog.displayOrderPlaceholder')"
              :disabled="!selectedFile || uploading"
            />
          </el-form-item>
        </el-form>

      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button 
            @click="cancelUpload"
            :disabled="uploading"
          >
            {{ $t('poster.uploadDialog.cancel') }}
          </el-button>
          <el-button 
            type="primary" 
            @click="confirmUpload"
            :loading="uploading"
            :disabled="!selectedFile || !uploadForm.fileName || uploading"
          >
            <el-icon v-if="uploading"><Loading /></el-icon>
            {{ uploading ? $t('poster.uploadDialog.uploading') : $t('poster.uploadDialog.confirmUpload') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Edit Dialog -->
    <el-dialog 
      v-model="showEditDialog" 
      :title="$t('poster.editDialog.title')"
      width="500px"
      :close-on-click-modal="false"
      class="upload-dialog"
    >
      <div class="upload-content">
        <!-- Image Preview -->
        <div class="preview-wrapper" v-if="editing">
          <ImagePreview 
            :src="editing.url || editing.avatar" 
            width="100%" 
            height="200px"
            fit="cover"
            :enable-preview="true"
          />
        </div>

        <!-- Edit Form -->
        <el-form :model="editForm" label-width="100px" class="upload-form">
          <el-form-item :label="$t('poster.editDialog.displayName')" prop="displayName">
            <el-input 
              v-model="editForm.displayName" 
              :placeholder="$t('poster.editDialog.displayNamePlaceholder')"
              clearable
            />
          </el-form-item>
          
          <el-form-item :label="$t('poster.editDialog.displayOrder')" prop="displayOrder">
            <el-input-number 
              v-model="editForm.displayOrder" 
              :min="0"
              :step="1"
              size="large"
            />
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button 
            @click="cancelEdit"
          >
            {{ $t('poster.editDialog.cancel') }}
          </el-button>
          <el-button 
            type="primary" 
            @click="updatePoster"
          >
            {{ $t('poster.editDialog.saveChanges') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Upload, Refresh, Picture, Delete, Document, List, Loading, Clock, Edit, Grid } from '@element-plus/icons-vue'
import ImagePreview from '@/components/ImagePreview.vue'
import { getSwiperImages, uploadSwiperImage, deleteSwiperImage, updateSwiperImage } from '@/api/image'
import { compressImage } from '@/utils/image-compressor'
import { imageCacheUtil } from '@/utils/image-cache'
import { useUserStore } from '@/stores/user'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const userStore = useUserStore()

const loading = ref(false)
const uploading = ref(false)
const showDialog = ref(false)
const showEditDialog = ref(false)
const viewMode = ref('card') // 'list' or 'card'
const posters = ref([])
const previewImage = ref('')
const selectedFile = ref(null)
const deleting = ref({})
const editing = ref(null)
const editForm = reactive({
  fileName: '',
  displayName: '',
  displayOrder: 0
})

const uploadForm = reactive({
  fileName: '',
  displayName: '',
  displayOrder: 0
})

// Single source of truth: Store only image metadata, NOT signed URLs
// The API will provide fresh signed URLs when needed
const posterStore = {
  data: [],
  lastFetchTime: 0,
  cacheDuration: 14 * 60 * 1000 // 14 minutes (slightly less than 15-min OSS expiry)
}

// Computed property that always returns fresh data with valid URLs
const getPostersWithFreshUrls = () => {
  const now = Date.now()
  
  // If cache is expired or empty, return current data (will trigger refresh)
  if (now - posterStore.lastFetchTime > posterStore.cacheDuration) {
    console.log('[Posters] Cache expired, will refresh on next access')
  }
  
  return posters.value
}

const loadPosters = async (forceRefresh = false) => {
  const now = Date.now()
  
  console.log('[Posters] loadPosters called, forceRefresh:', forceRefresh)
  
  // Check if we need to refresh (cache expired or force refresh)
  if (!forceRefresh && posterStore.data.length > 0 && 
      (now - posterStore.lastFetchTime) < posterStore.cacheDuration) {
    console.log('[Posters] Using cached data')
    return
  }
  
  loading.value = true
  try {
    // If force refresh, clear cached image data
    if (forceRefresh) {
      console.log('[Posters] Clearing cached image data...')
      imageCacheUtil.clearAllCachedImages()
    }
    
    const tenantId = userStore.userInfo?.tenantId || 1
    console.log('[Posters] Fetching from API...')
    const res = await getSwiperImages(tenantId)
    
    console.log('[Posters] API response:', res.success ? 'SUCCESS' : 'FAILED')
    
    if (res.success && res.data) {
      console.log('[Posters] Received', res.data.length, 'posters from API')
      
      // Single source of truth: Store the data
      posters.value = res.data.map((item, index) => ({
        id: item.id || index + 1,
        displayName: item.displayName || item.fileName || `Swiper ${index + 1}`,
        fileName: item.fileName,
        storagePath: item.storagePath,
        url: item.url, // API provides fresh signed URL
        avatar: item.url, // Keep both for compatibility
        displayOrder: item.displayOrder,
        createdAt: item.createdAt
      }))
      
      console.log('[Posters] Posters array updated, first URL:', posters.value[0]?.url?.substring(0, 80))
      
      // Update store metadata
      posterStore.data = posters.value
      posterStore.lastFetchTime = now
      
      console.log('[Posters] Loaded', posters.value.length, 'posters')
      console.log('[Posters] Images will be cached when they load in the browser')
    } else {
      console.warn('[Posters] API returned no data')
      posters.value = []
      posterStore.data = []
    }
  } catch (error) {
    console.error('Failed to load posters:', error)
    ElMessage.error(t('poster.errors.loadFailed'))
  } finally {
    loading.value = false
  }
}

// Handle file selection
const handleFileSelect = (event) => {
  const file = event.raw || event.target?.files?.[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    ElMessage.error(t('poster.errors.selectImageFile'))
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    ElMessage.error(t('poster.errors.fileSizeExceeded'))
    return
  }

  selectedFile.value = file
  
  const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
  uploadForm.fileName = file.name // Include file extension
  uploadForm.displayName = fileNameWithoutExt

  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    previewImage.value = e.target.result
  }
  reader.readAsDataURL(file)
}

// Remove image
const removeImage = () => {
  selectedFile.value = null
  previewImage.value = ''
  uploadForm.fileName = ''
  uploadForm.displayName = ''
  uploadForm.displayOrder = 0
}

// Show upload dialog
const openUploadDialog = () => {
  uploadForm.fileName = ''
  uploadForm.displayName = ''
  uploadForm.displayOrder = 0
  previewImage.value = ''
  selectedFile.value = null
  showDialog.value = true
}

// Cancel upload
const cancelUpload = () => {
  showDialog.value = false
  uploadForm.fileName = ''
  uploadForm.displayName = ''
  uploadForm.displayOrder = 0
  previewImage.value = ''
  selectedFile.value = null
}

// Confirm upload
const confirmUpload = async () => {
  if (!selectedFile.value) {
    ElMessage.warning(t('poster.errors.selectFile'))
    return
  }

  uploading.value = true
  try {
    const tenantId = userStore.userInfo?.tenantId || 1
    
    let fileToUpload = selectedFile.value
    if (selectedFile.value.size > 170 * 1024) {
      ElMessage.info(t('poster.info.compressing'))
      fileToUpload = await compressImage(selectedFile.value, 0.17, 1920)
    }
    
    const res = await uploadSwiperImage(tenantId, {
      file: fileToUpload,
      fileName: uploadForm.fileName,
      displayName: uploadForm.displayName,
      displayOrder: uploadForm.displayOrder
    })
    
    const response = res.value || res
    
    if (response.success) {
      ElMessage.success(t('poster.confirm.uploadSuccess'))
      showDialog.value = false
      loadPosters()
    } else {
      ElMessage.error(response.message || t('poster.confirm.uploadFailed'))
    }
  } catch (error) {
    console.error('Failed to upload poster:', error)
    ElMessage.error(t('poster.confirm.uploadFailed'))
  } finally {
    uploading.value = false
  }
}

// Delete poster
const deletePoster = async (poster) => {
  try {
    await ElMessageBox.confirm(
      t('poster.confirm.deleteMessage'),
      t('poster.confirm.deleteTitle'),
      {
        confirmButtonText: t('poster.actions.delete'),
        cancelButtonText: t('poster.uploadDialog.cancel'),
        type: 'warning'
      }
    )

    deleting.value[poster.id] = true
    const tenantId = userStore.userInfo?.tenantId || 1
    const res = await deleteSwiperImage(tenantId, poster.id)
    
    if (res.success) {
      ElMessage.success(t('poster.confirm.deleteSuccess'))
      loadPosters(true) // Force refresh to get fresh data
    } else {
      ElMessage.error(res.message || t('poster.confirm.deleteFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to delete poster:', error)
      ElMessage.error(t('poster.confirm.deleteFailed'))
    }
  } finally {
    deleting.value[poster.id] = false
  }
}

// Format date
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('poster.time.justNow')
  if (minutes < 60) return t('poster.time.minutesAgo', { count: minutes })
  if (hours < 24) return t('poster.time.hoursAgo', { count: hours })
  if (days < 7) return t('poster.time.daysAgo', { count: days })
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Edit poster
const editPoster = (poster) => {
  editing.value = poster
  editForm.fileName = poster.fileName || ''
  editForm.displayName = poster.name || ''
  editForm.displayOrder = poster.displayOrder || 0
  showEditDialog.value = true
}

// Update poster
const updatePoster = async () => {
  if (!editing.value) return

  try {
    const tenantId = userStore.userInfo?.tenantId || 1
    const res = await updateSwiperImage(tenantId, editing.value.id, editForm)
    
    if (res.success) {
      ElMessage.success(t('poster.confirm.updateSuccess'))
      loadPosters(true) // Force refresh to get fresh data
      showEditDialog.value = false
    } else {
      ElMessage.error(res.message || t('poster.confirm.updateFailed'))
    }
  } catch (error) {
    console.error('Failed to update poster:', error)
    ElMessage.error(t('poster.confirm.updateFailed'))
  }
}

// Cancel edit
const cancelEdit = () => {
  showEditDialog.value = false
  editing.value = null
  editForm.fileName = ''
  editForm.displayName = ''
  editForm.displayOrder = 0
}

// Watch for view mode changes
watch(viewMode, (newMode, oldMode) => {
  console.log(`[Posters] View mode changed: ${oldMode} -> ${newMode}`)
  // No automatic refresh - user must click refresh button manually
})

onMounted(() => {
  loadPosters()
})
</script>

<style scoped lang="scss">
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

// Swiper Demo Section
.swiper-demo-section {
  margin: 20px 0;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);

  .demo-header {
    text-align: center;
    margin-bottom: 20px;

    .demo-title {
      font-size: 20px;
      font-weight: 600;
      color: #ffffff;
      margin: 0 0 8px 0;
    }

    .demo-subtitle {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .swiper-container {
    max-width: 375px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 20px;
    padding: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

    :deep(.el-carousel) {
      border-radius: 12px;
      overflow: hidden;

      .el-carousel__container {
        border-radius: 12px;
      }

      .el-carousel__indicators--outside {
        bottom: -30px;

        .el-carousel__button {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
        }

        .is-active .el-carousel__button {
          background-color: #409eff;
        }
      }

      .el-carousel__arrow {
        background-color: rgba(255, 255, 255, 0.8);
        color: #409eff;

        &:hover {
          background-color: rgba(255, 255, 255, 1);
        }
      }
    }

    .swiper-slide {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f7fa;
      border-radius: 12px;
      overflow: hidden;

      :deep(.image-preview-wrapper),
      :deep(.image-preview-container) {
        width: 100% !important;
        height: 100% !important;
      }

      :deep(.preview-img) {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
      }

      .slide-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);

        .placeholder-icon {
          font-size: 48px;
          color: #c0c4cc;
          margin-bottom: 12px;
        }

        .placeholder-text {
          font-size: 14px;
          color: #909399;
          font-weight: 500;
        }
      }
    }
  }
}

.posters-grid {
  padding: 12px;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .empty-content {
    .empty-icon {
      font-size: 64px;
      color: #c0c4cc;
      margin-bottom: 16px;
    }

    .empty-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 8px 0;
    }

    .empty-description {
      font-size: 14px;
      color: #909399;
      margin: 0 0 24px 0;
    }
  }
}

.poster-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.poster-card {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }

  .poster-image-wrapper {
    position: relative;
    width: 100%;
    height: 200px;
    background-color: #f5f7fa;
    overflow: hidden;
  }

  .image-container {
    width: 100%;
    height: 100%;
    overflow: hidden;

    :deep(.image-preview-wrapper) {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }

    :deep(.image-preview-container) {
      width: 100% !important;
      height: 100% !important;
    }

    :deep(.preview-img) {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
  }

  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);

    .placeholder-icon {
      font-size: 48px;
      color: #c0c4cc;
      margin-bottom: 12px;
    }

    .placeholder-text {
      font-size: 14px;
      color: #909399;
      font-weight: 500;
    }
  }

  .poster-info {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .poster-name {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .poster-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #606266;

        .el-icon {
          color: #909399;
          font-size: 14px;
        }
      }
    }

    .poster-actions {
      padding-top: 12px;
      border-top: 1px solid #f0f2f5;

      .action-buttons {
        display: flex;
        gap: 8px;

        .el-button {
          flex: 1;
          height: 36px;
          font-size: 13px;
          font-weight: 500;
          border-radius: 6px;
        }
      }
    }
  }
}

.upload-dialog {
  :deep(.el-dialog__header) {
    padding: 24px 24px 20px;
    border-bottom: 1px solid #f0f2f5;

    .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  :deep(.el-dialog__body) {
    padding: 24px;
  }

  .upload-content {
    .upload-area {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 20px;

      .upload-trigger {
        width: 100%;

        .upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 300px;
          border: 2px dashed #dcdfe6;
          border-radius: 8px;
          background-color: #f5f7fa;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            border-color: #409eff;
            background-color: #ecf5ff;

            .el-icon {
              color: #409eff;
            }
          }

          .el-icon {
            font-size: 64px;
            color: #c0c4cc;
            margin-bottom: 16px;
            transition: all 0.3s ease;
          }

          .upload-text {
            font-size: 14px;
            color: #606266;
            margin-bottom: 8px;
            font-weight: 500;
          }

          .upload-hint {
            font-size: 12px;
            color: #909399;
          }
        }

        :deep(.el-upload-dragger) {
          width: 100%;
          height: 300px;
          border: 2px dashed #dcdfe6;
          border-radius: 8px;
          background-color: #f5f7fa;

          &:hover {
            border-color: #409eff;
            background-color: #ecf5ff;
          }

          &.is-dragover {
            border-color: #409eff;
            background-color: #ecf5ff;
          }
        }
      }
    }

    .preview-wrapper {
      position: relative;
      margin-bottom: 20px;
      padding: 16px;
      background: #f5f7fa;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: center;

      .preview-image {
        max-width: 100%;
        max-height: 400px;
        border-radius: 8px;
      }

      .remove-image-btn {
        height: 32px;
        padding: 0 16px;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(245, 108, 108, 0.3);
        }
      }
    }

    .upload-form {
      margin-top: 20px;

      .el-form-item {
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        :deep(.el-input__wrapper) {
          border-radius: 6px;

          &:hover {
            box-shadow: 0 0 0 1px #409eff inset;
          }

          &.is-focus {
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
          }
        }

        :deep(.el-input-number) {
          .el-input-number__decrease,
          .el-input-number__increase {
            background-color: #f5f7fa;
            border-color: #dcdfe6;
            color: #606266;

            &:hover {
              color: #409eff;
            }
          }
        }
      }
    }
  }

  :deep(.el-dialog__footer) {
    padding: 16px 24px;
    border-top: 1px solid #f0f2f5;
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    .el-button {
      flex: 0 0 120px;
      height: 40px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:not(:disabled) {
        &:hover {
          transform: translateY(-2px);
        }

        &:active {
          transform: translateY(0);
        }
      }

      &.el-button--primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

        &:hover:not(:disabled) {
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
        }
      }
    }
  }
}

// Mobile responsive styles
@media (max-width: 768px) {
  .page-container {
    padding: 12px;

    :deep(.el-card) {
      :deep(.el-card__header) {
        padding: 16px;

        .card-header {
          flex-direction: column;
          align-items: stretch;
          gap: 12px;

          span {
            font-size: 18px;
            text-align: center;
          }

          .header-actions-wrapper {
            display: flex;
            flex-direction: column;
            gap: 10px;
            align-items: center;
          }

          .header-actions {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 8px;
            width: 100%;

            .el-button {
              flex: 1;
              min-width: 120px;
              max-width: 200px;
            }
          }

          .view-toggle {
            .el-button {
              padding: 8px 12px;
            }
          }
        }
      }

      :deep(.el-card__body) {
        padding: 12px;
      }
    }
  }

  // Swiper Demo Mobile Styles
  .swiper-demo-section {
    margin: 12px 0;
    padding: 16px;

    .demo-header {
      margin-bottom: 16px;

      .demo-title {
        font-size: 18px;
      }

      .demo-subtitle {
        font-size: 13px;
      }
    }

    .swiper-container {
      max-width: 100%;
      padding: 8px;

      :deep(.el-carousel) {
        .el-carousel__indicators--outside {
          bottom: -24px;

          .el-carousel__button {
            width: 6px;
            height: 6px;
          }
        }

        .el-carousel__arrow {
          width: 32px;
          height: 32px;

          .el-icon {
            font-size: 14px;
          }
        }
      }
    }
  }

  .posters-grid {
    padding: 0;

    .poster-cards {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .poster-card {
      .poster-image-wrapper {
        height: 200px;
      }

      .poster-info {
        padding: 16px;

        .poster-name {
          font-size: 16px;
          margin-bottom: 8px;
        }

        .poster-meta {
          margin-bottom: 12px;
          
          .meta-item {
            font-size: 13px;
            
            .el-icon {
              font-size: 14px;
            }
          }
        }

        .poster-actions {
          padding-top: 12px;

          .action-buttons {
            display: flex;
            flex-direction: row;
            gap: 8px;

            .el-button {
              flex: 1;
              height: 40px;
              font-size: 14px;
            }
          }
        }
      }
    }
  }

  // Mobile table optimizations
  :deep(.el-table) {
    font-size: 13px;

    :deep(.el-table__cell) {
      padding: 10px 8px;
    }

    :deep(.el-button--link) {
      padding: 6px 10px;
      font-size: 13px;
    }
  }
}

// Small mobile devices
@media (max-width: 480px) {
  .page-container {
    padding: 8px;

    :deep(.el-card) {
      :deep(.el-card__header) {
        padding: 12px;

        .card-header {
          span {
            font-size: 16px;
          }

          .header-actions-wrapper {
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: center;
          }

          .header-actions {
            gap: 6px;
            width: 100%;

            .el-button {
              padding: 8px 12px;
              font-size: 13px;
              min-width: 100px;
            }
          }

          .view-toggle {
            .el-button {
              padding: 8px 10px;
              min-width: 40px;
            }
          }
        }
      }
    }
  }

  :deep(.el-table) {
    :deep(.el-table__cell) {
      padding: 8px 6px;
    }
  }

  .posters-grid {
    padding: 0;

    .poster-card {
      .poster-image-wrapper {
        height: 180px;
      }

      .poster-info {
        padding: 12px;

        .poster-name {
          font-size: 15px;
        }

        .poster-meta {
          .meta-item {
            font-size: 12px;
          }
        }

        .poster-actions {
          .action-buttons {
            .el-button {
              height: 38px;
              font-size: 13px;
            }
          }
        }
      }
    }
  }
}

// Date cell styling
.date-cell {
  line-height: 1.5;
  word-wrap: break-word;
  white-space: normal;
}
</style>